import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { SourceImageApi } from "@/lib/api/source-image-api";
import { SourceImageDto } from "@repo/api/source-image";
import { downloadFile } from "@/lib/utils";
import Toast from "@/lib/components/Toast";
import { Transition, usePresenceData } from "motion/react";
import { ActionIdentifier } from "@/lib/stores/editor-store/creative-project-state";
import { emitInspirationAction } from "./Inspiration/inspiration-action-events";

export type SourceImageKey =
  | "startSourceImageExternalId"
  | "endSourceImageExternalId";

export function useSourceImageActions(
  sourceImageKey: SourceImageKey,
  options?: { onImageReplaced?: () => void },
) {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const replaceCurrentTimelineItemSourceImage = useEditorStore(
    (state) => state.service.replaceCurrentTimelineItemSourceImage,
  );
  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const sourceImage = useEditorStore((state) =>
    state.service.getSourceImageByExternalId(timelineItem?.[sourceImageKey]),
  );
  const uploadSourceImages = useEditorStore(
    (state) => state.service.uploadSourceImages,
  );

  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);

  const onImageReplaced = options?.onImageReplaced;

  const handleReplaceSelect = (sourceImage: SourceImageDto) => {
    emitInspirationAction(
      sourceImageKey === "startSourceImageExternalId"
        ? ActionIdentifier.ADD_START_IMAGE
        : ActionIdentifier.ADD_END_IMAGE,
    );
    replaceCurrentTimelineItemSourceImage(sourceImageKey, sourceImage);
    setIsReplaceDialogOpen(false);
    onImageReplaced?.();
  };

  const handleRemoveImage = () => {
    if (!timelineItem || sourceImageKey !== "endSourceImageExternalId") return;

    updateVideo((video) => {
      const item = video.compositionJson.mainSceneTimeline.find(
        (item) => item.externalId === timelineItem.externalId,
      );
      if (!item) return;

      item.endSourceImageExternalId = undefined;
    });
  };

  const handleDownloadImage = async () => {
    if (!sourceImage?.externalId) {
      Toast.error("Image not available for download");
      return;
    }

    try {
      const { downloadUrl } = await SourceImageApi.getDownloadUrl(
        teamExternalId,
        sourceImage.externalId,
      );
      downloadFile(downloadUrl);
    } catch (error) {
      console.error(error);
      Toast.error("Failed to download image");
    }
  };

  const { open: openUploadDialog, getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    multiple: false,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
    },
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      try {
        const uploaded = await uploadSourceImages([file]);
        const sourceImage = uploaded[0];
        if (sourceImage) {
          handleReplaceSelect(sourceImage);
        }
      } catch (err) {
        console.error(err);
        Toast.error("Failed to upload image");
      }
    },
  });

  return {
    sourceImage,
    isReplaceDialogOpen,
    setIsReplaceDialogOpen,
    handleReplaceSelect,
    handleRemoveImage,
    handleDownloadImage,
    openUploadDialog,
    getInputProps,
  };
}

export type MainViewState =
  | { mode: "ANIMATION"; sourceImageKey: SourceImageKey }
  | {
      mode: "EDIT_IMAGE";
      sourceImageKey: SourceImageKey;
      isSelectionConfirmed?: boolean;
    }
  | { mode: "PROMPT_LIBRARY"; source: "ANIMATION" }
  | {
      mode: "PROMPT_LIBRARY";
      source: "EDIT_IMAGE";
      sourceImageKey: SourceImageKey;
    }
  | { mode: "ELEMENTS"; sourceImageKey: SourceImageKey };

export type SidebarViewState =
  | { mode: "LIST" }
  | { mode: "GUIDE"; guideId: string };

export type ViewStateOf<
  T extends { mode: string },
  M extends T["mode"],
> = Extract<T, { mode: M }>;

export type ViewTransition = "SLIDE_FROM_LEFT" | "SLIDE_FROM_RIGHT";

export type SetView<T extends { mode: string }> = (
  viewState: T,
  transition?: ViewTransition,
) => void;
export type GoBack = (transition?: ViewTransition) => void;

export type UpdateCurrentView<T extends { mode: string }> = (
  updater: (view: T) => T,
) => void;

export function useViewHistory<T extends { mode: string }>(defaultView: T) {
  const [viewHistory, setViewHistory] = useState<T[]>([defaultView]);
  const [transition, setTransition] = useState<ViewTransition | undefined>();
  const view = viewHistory[viewHistory.length - 1];

  if (!view) throw new Error("View is not set");

  const setView = useCallback((nextView: T, transition?: ViewTransition) => {
    setTransition(transition);
    setViewHistory((currentHistory) => {
      const currentView = currentHistory[currentHistory.length - 1];

      if (!currentView) {
        throw new Error("Current view is not set");
      }

      if (currentView.mode === nextView.mode) {
        return currentHistory;
      }

      return [...currentHistory, nextView];
    });
  }, []);

  const goBack = useCallback((transition?: ViewTransition) => {
    setTransition(transition);
    setViewHistory((currentHistory) =>
      currentHistory.length > 1 ? currentHistory.slice(0, -1) : currentHistory,
    );
  }, []);

  const updateCurrentView = useCallback((updater: (view: T) => T) => {
    setViewHistory((currentHistory) => {
      const currentView = currentHistory[currentHistory.length - 1];
      if (!currentView) {
        throw new Error("Current view is not set");
      }
      return [...currentHistory.slice(0, -1), updater(currentView)];
    });
  }, []);

  return { view, transition, setView, goBack, updateCurrentView };
}

export type PresenceData = {
  currentMode: string;
  transition?: ViewTransition;
};

export type ViewTransitionProps = {
  custom: PresenceData | undefined;
  transition: Transition;
  variants: {
    initial: (data: PresenceData) => { x: string | number };
    animate: { x: number };
    exit: (data: PresenceData) => { x: string | number };
  };
  initial: "initial" | false;
  animate: "animate";
  exit: "exit";
};

const NORMAL_POSITION = { x: 0 };

export function useViewTransitionProps(options?: {
  duration?: number;
}): ViewTransitionProps {
  const presenceData: PresenceData | undefined = usePresenceData();
  const hasTransition = presenceData?.transition !== undefined;

  return {
    custom: presenceData,
    transition: { type: "tween", duration: options?.duration ?? 0.3 },
    variants: {
      initial: (data: PresenceData | undefined) => {
        if (!data?.transition) return NORMAL_POSITION;
        const fromLeft = data.transition === "SLIDE_FROM_LEFT";
        return { x: fromLeft ? "-100%" : "100%" };
      },
      animate: NORMAL_POSITION,
      exit: (data: PresenceData | undefined) => {
        if (!data?.transition) return NORMAL_POSITION;
        const fromLeft = data.transition === "SLIDE_FROM_LEFT";
        return { x: fromLeft ? "100%" : "-100%" };
      },
    },
    initial: hasTransition ? "initial" : false,
    animate: "animate",
    exit: "exit",
  };
}
